/**
 * Separation-of-duties helper for the safety form review/coaching flows (WI 30201, shape C).
 *
 * Returns whether a submission should be treated as the logged-in user's OWN for the purpose
 * of the "you may not review/coach/approve your own submission" guards.
 *
 * It fails CLOSED on an unresolved identity: when `loggedInUserId` is falsy (the
 * `UserService.myInfo` profile never resolved, or resolved with a falsy `UserId`), we cannot
 * prove the record is NOT the user's own, so we treat it as own and return `true`. Callers gate
 * the privileged self-action on `!isOwn`, so an unknown identity is denied that action until the
 * profile resolves, instead of the previous behaviour where `submittedUserId === undefined`
 * evaluated to `false` and silently granted a self-review/coach/approve.
 *
 * Do NOT use this for "did I author this review" reads (e.g. reading back your own approval
 * comments): that question must fail closed the other way (unknown identity should NOT grant a
 * self-read), which is a plain `?.` comparison, not this helper.
 */
export function isOwnOrUnresolvedUser(
  submittedUserId: string | null | undefined,
  loggedInUserId: string | null | undefined
): boolean {
  if (!loggedInUserId) {
    // Unresolved logged-in identity: fail closed (treat as own, deny the self-action).
    return true;
  }
  return submittedUserId === loggedInUserId;
}
